import React, { useState, useEffect, useRef } from 'react';

export function Grain() {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 240'>
<filter id='n'>
<feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/>
<feColorMatrix values='0 0 0 0 0.95   0 0 0 0 0.85   0 0 0 0 0.6   0 0 0 0.5 0'/>
</filter>
<rect width='100%' height='100%' filter='url(%23n)'/>
</svg>`;
  const url = `data:image/svg+xml;utf8,${svg.replace(/#/g, '%23').replace(/\n/g, '')}`;
  return <div className="grain" style={{ backgroundImage: `url("${url}")` }} aria-hidden="true" />;
}

export function Letterbox() {
  return (
    <>
      <div className="letterbox top" aria-hidden="true" />
      <div className="letterbox bottom" aria-hidden="true" />
    </>
  );
}

export function Halo({ children, intensity = 1, scale = 1 }) {
  return (
    <div className="halo-wrap" style={{ transform: `scale(${scale})` }}>
      <div className="halo-ring" style={{ opacity: 0.4 * intensity }} />
      <div style={{ position: "relative", zIndex: 2 }}>{children}</div>
    </div>
  );
}

export function useScrollProgress() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setP(max > 0 ? window.scrollY / max : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return p;
}

export function FadeIn({ children, delay = 0, as: Tag = "div", className = "", style = {}, direction = "up" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const scrollPercent = (window.innerHeight - rect.top) / window.innerHeight;
        if (scrollPercent > 0 && scrollPercent < 2) {
          setOffset(scrollPercent * 30); // Parallax factor
        }
      }
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setTimeout(() => setVisible(true), delay);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    if (ref.current) io.observe(ref.current);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      io.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [delay]);

  const directions = {
    up: `translateY(${visible ? 0 : 30}px)`,
    down: `translateY(${visible ? 0 : -30}px)`,
    left: `translateX(${visible ? 0 : 30}px)`,
    right: `translateX(${visible ? 0 : -30}px)`,
    none: 'none'
  };

  const combinedStyle = {
    ...style,
    opacity: visible ? 1 : 0,
    transform: `${directions[direction]} translateY(${-offset}px)`,
    transition: `opacity 1.2s cubic-bezier(0.2, 0.8, 0.2, 1), transform 1.2s cubic-bezier(0.2, 0.8, 0.2, 1)`,
    willChange: 'opacity, transform'
  };

  return (
    <Tag ref={ref} className={`fade-in-v2 ${className}`} style={combinedStyle}>
      {children}
    </Tag>
  );
}

export function ChapterMark({ num, yuga, deva, name, meaning }) {
  return (
    <FadeIn className="chapter-mark">
      <span className="num">Chapter {num}</span>
      <span className="yuga">{yuga}</span>
      <h2 className="deva-title">{deva}</h2>
      <h3 className="name">{name}</h3>
      <p className="meaning">{meaning}</p>
    </FadeIn>
  );
}
